/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BuilderUserContext } from '@gh/builder-shared';
import { ChangeStatusParams, TodoStatus, UserRestService } from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import {
  AutocompleteComponent,
  AutocompleteLoadMoreEvent,
  DialogInput,
  getControl,
  getControlValue,
  NotificationService,
  requiredFormValidator,
  WaitingService,
} from '@gh/core-ui';
import {
  TodoChangeStatusResponse,
  TodoDetails,
  TodoList,
  TodoListBusinessService,
} from '../../../resources';

export interface PassTodoConfig<T> {
  todo?: T;
  selectedRows: T[];
  selectedUserId: ID;
}

@Component({
  standalone: false,
  selector: 'gh-pass-todo-dialog',
  templateUrl: 'pass-todo-dialog.component.html',
  styleUrls: ['pass-todo-dialog.component.scss'],
})
export class PassTodoDialogComponent implements DialogInput<PassTodoConfig<TodoList>>, OnInit {
  mutateForm: FormGroup;
  userAutocomplete: AutocompleteComponent;
  input: PassTodoConfig<TodoList>;

  private currentTodo: TodoDetails;

  constructor(private dialogRef: MatDialogRef<PassTodoDialogComponent>,
              private todoListBusiness: TodoListBusinessService,
              private userDataService: UserRestService,
              private userContext: BuilderUserContext,
              private notificationService: NotificationService,
              private formBuilder: FormBuilder,
              private waitingService: WaitingService) {
    this.mutateForm = this.formBuilder.group({
      assignee: [void 0, requiredFormValidator],
    });
  }

  ngOnInit(): void {
    const { todo } = this.input;
    if (todo) {
      this.todoListBusiness.get(todo.id).pipe(
        this.waitingService.operator<TodoDetails>())
        .subscribe((responseTodo: TodoDetails) => {
          this.mutateForm.setValue({
            assignee: { fullName: '', userId: void 0 },
          });
          this.currentTodo = responseTodo;
        });
    }
  }

  loadMoreUsers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const fullQuery = {
      ...query,
      filter: {
        name: query.filter,
        showInactive: false,
        excludeIds: [this.input.selectedUserId],
      },
    };
    load(this.todoListBusiness.getAutocompleteUser(fullQuery));
  }

  onSubmit(): void {
    if (this.mutateForm.invalid) {
      getControl(this.mutateForm, 'assignee').markAsTouched();
      return;
    }

    const { todo, selectedRows } = this.input;
    const submitMessage = 'msg_saved_successfully';
    const success = (status: TodoChangeStatusResponse) => {
      this.notificationService.success(submitMessage);
      this.dialogRef.close(status);
    };

    const passTodos = todo ? [todo] : selectedRows.filter((item) => !item.activityTodo);
    const changeStatusParams: ChangeStatusParams = {
      fromStatus: TodoStatus.INBOX,
      toStatus: TodoStatus.PASSED,
      todoList: passTodos,
      userId: getControlValue(this.mutateForm, 'assignee'),
    };
    this.todoListBusiness.changeStatus(changeStatusParams)
      .subscribe({ next: success, error: this.notificationService.processAll });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
